import React, { useState } from "react";
import {
  useNavigate,
  Link,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useTheme } from "../../../contexts/ThemeContext";
import { useAppSelector } from "../../../redux/hooks";
import { RootState } from "../../../redux/store";
import { adminRoutes } from "../../../routers/AdminRouters";
import {
  useGetSessionFormatQuery,
  useGetSessionFormatBloacksBySessionFormatIdQuery,
  useCreateSessionMatriceBlocksMutation,
} from "../../../services";
import MatricSessionFormat from "./exercisebuildercomponents/MetricSessionFormat";
import { UnsavedChangesModal } from "./modals";
import { toast } from "../../../core/toast";

const SessionMetricsBuilder: React.FC = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  type NavbarSliceState = { isOpen: boolean };
  const isSidebarOpen = useAppSelector(
    (state: RootState) => (state.navbar as NavbarSliceState).isOpen,
  );
  const sidebarWidth = isSidebarOpen ? 256 : 64;
  const [searchParams] = useSearchParams();
  const categoryId = searchParams.get("category");
  const { sessionid } = useParams<{ sessionid?: string }>();

  const { data: sessionFormat } = useGetSessionFormatQuery(sessionid ?? "");
  const formatList = sessionFormat?.results || [];

  const [selectedFormatIdx, setSelectedFormatIdx] = useState(0);
  const selectedFormat = formatList[selectedFormatIdx];
  const selectedFormatId = selectedFormat?._id;

  const [isDirty, setIsDirty] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [pendingAction, setPendingAction] = useState<{
    type: "FORMAT" | "DIFFICULTY" | "CANCEL" | "PREVIOUS" | "SUMMARY";
    index?: number;
    difficulty?: "BEGINNER" | "ADVANCED";
  } | null>(null);
  const [hasTimeErrors, setHasTimeErrors] = useState(false);
  const [showValidationErrors, setShowValidationErrors] = useState(false);

  // whether the selected format exposes difficulties (subformats)
  const selectedFormatHasDifficulties =
    Array.isArray(selectedFormat?.format?.difficulties) &&
    selectedFormat.format.difficulties.length > 0;

  // Track optional difficulty filter
  const [selectedDifficulty, setSelectedDifficulty] = useState<
    "BEGINNER" | "ADVANCED" | "GENERAL" | null
  >(null);

  // Initialize selectedDifficulty from URL query param (if provided)
  React.useEffect(() => {
    const q = searchParams.get("difficulty");
    if (q === "BEGINNER" || q === "ADVANCED" || q === "GENERAL") {
      setSelectedDifficulty(q as any);
    }
  }, [searchParams]);

  // Determine effective difficulty to use across queries/UI/submission:
  // - If the format has difficulties, keep the selectedDifficulty (may be null)
  // - If the format does NOT have difficulties, use 'GENERAL'
  const effectiveDifficulty: "BEGINNER" | "ADVANCED" | "GENERAL" | null =
    selectedFormatHasDifficulties ? selectedDifficulty : "GENERAL";

  const { data: sessionFormatBlocks, refetch: refetchFormatBlocks } =
    useGetSessionFormatBloacksBySessionFormatIdQuery(
      selectedFormatId
        ? {
          sessionFormatId: selectedFormatId,
          difficulty: effectiveDifficulty ?? undefined,
        }
        : { sessionFormatId: "", difficulty: undefined },
      { skip: !selectedFormatId },
    );
  const isSessionFormatBlocksExist = React.useMemo(() => {
    return (sessionFormatBlocks?.results || []).some((blk: any) =>
      (blk.workouts || []).some((w: any) => Array.isArray(w.metrics) && w.metrics.length > 0)
    );
  }, [sessionFormatBlocks]);
  const [createMatrice, { isLoading: isSubmitting }] =
    useCreateSessionMatriceBlocksMutation();

  React.useEffect(() => {
    if (selectedFormatId) {
      refetchFormatBlocks();
    }
  }, [selectedFormatId, effectiveDifficulty, refetchFormatBlocks]);

  const themeBox = theme === "dark";
  const borderColor = themeBox ? "#232323" : "#E5E7EB";
  const bgCard = themeBox
    ? "bg-[#121212] border-[#232323]"
    : "bg-white border-gray-200";

  const [formatBlocks, setFormatBlocks] = useState<
    {
      id: string;
      name: string;
      fields: string[];
      fieldValues?: Record<string, number>;
      workPeriod?: string;
      restPeriod?: string;
      blockTiming?: string;
      restTime?: string;
      type?: "MATRIX" | "GROUP" | "SINGLE";
      workouts?: any[];
    }[]
  >([]);

  // HH:MM:SS -> seconds
  const toSeconds = (v: string | number | undefined): number => {
    if (v === undefined) return 0;
    if (typeof v === "number") return v;
    const parts = v.split(":").map((n) => Number(n));
    if (parts.length === 3) {
      const [h, m, s] = parts;
      return (
        (isNaN(h) ? 0 : h) * 3600 + (isNaN(m) ? 0 : m) * 60 + (isNaN(s) ? 0 : s)
      );
    }
    if (parts.length === 2) {
      const [m, s] = parts;
      return (isNaN(m) ? 0 : m) * 60 + (isNaN(s) ? 0 : s);
    }
    const n = Number(v);
    return isNaN(n) ? 0 : n;
  };

  // Build a lookup: workoutId -> UI block (formatBlocks entry)
  // For GROUP blocks, also index nested workouts by their _id
  const uiByWorkoutId = React.useMemo(() => {
    const map = new Map<string, (typeof formatBlocks)[number]>();
    formatBlocks.forEach((b) => {
      map.set(b.id, b);
      // For GROUP blocks, index nested workouts too
      if (b.type === "GROUP" && Array.isArray(b.workouts)) {
        b.workouts.forEach((w: any) => {
          if (w._id) map.set(String(w._id), w);
        });
      }
    });
    return map;
  }, [formatBlocks]);

  // Build a lookup: groupBlockId -> GROUP formatBlock entry
  const groupBlockById = React.useMemo(() => {
    const map = new Map<string, (typeof formatBlocks)[number]>();
    formatBlocks.forEach((b) => {
      if (b.type === "GROUP") map.set(b.id, b);
    });
    return map;
  }, [formatBlocks]);

  // Build a lookup: parent block id -> restTime (from any UI workout under that block)
  const restTimeByParentBlock = React.useMemo(() => {
    const map = new Map<string, number>();
    // Flatten API workouts with parent block id and workout _id
    (sessionFormatBlocks?.results || []).forEach((blk: any) => {
      const parentId = String(blk._id);
      const blockType = blk.type || "MATRIX";

      // For GROUP blocks, check the GROUP entry in formatBlocks directly
      if (blockType === "GROUP") {
        const groupEntry = groupBlockById.get(parentId);
        if (groupEntry?.restTime) {
          map.set(parentId, toSeconds(groupEntry.restTime));
        } else {
          map.set(
            parentId,
            typeof blk.restTime === "string"
              ? toSeconds(blk.restTime)
              : Number(blk.restTime || 0),
          );
        }
        return;
      }

      const workouts = Array.isArray(blk.workouts) ? blk.workouts : [];
      // find first workout with a UI entry that has restTime
      for (const w of workouts) {
        const workoutId = String(w._id);
        const ui = uiByWorkoutId.get(workoutId);
        if (ui?.restTime) {
          map.set(parentId, toSeconds(ui.restTime));
          break;
        }
      }
      // fallback to API restTime if no UI restTime found
      if (!map.has(parentId)) {
        map.set(
          parentId,
          typeof blk.restTime === "string"
            ? toSeconds(blk.restTime)
            : Number(blk.restTime || 0),
        );
      }
    });
    return map;
  }, [sessionFormatBlocks?.results, uiByWorkoutId, groupBlockById]);

  const allBlocksHaveMetrics = (blocks: any[]) => {
    if (blocks.length === 0) return false;
    return blocks.every((block) => {
      if (block.type === "GROUP") {
        return (block.workouts || []).every(
          (w: any) => Array.isArray(w.metrics) && w.metrics.length > 0,
        );
      }
      return Array.isArray(block.fields) && block.fields.length > 0;
    });
  };

  const allTimesFilled = (blocks: any[]) => {
    if (blocks.length === 0) return true;
    return blocks.every((block) => {
      if (block.type === "GROUP") {
        return (block.workouts || []).every(
          (w: any) =>
            w.workPeriod &&
            w.workPeriod !== "00:00:00" &&
            w.restPeriod &&
            w.restPeriod !== "00:00:00",
        );
      }
      return (
        block.workPeriod &&
        block.workPeriod !== "00:00:00" &&
        block.restPeriod &&
        block.restPeriod !== "00:00:00"
      );
    });
  };

  const onSaveMatrix = async (overridingAction?: typeof pendingAction) => {
    if (!selectedFormatId) return;

    if (!allBlocksHaveMetrics(formatBlocks) || !allTimesFilled(formatBlocks)) {
      setShowValidationErrors(true);
      if (!allTimesFilled(formatBlocks)) {
        toast.error("Please ensure all workout work and rest periods have valid time values.");
      }
      return false;
    }

    if (hasTimeErrors) {
      toast.error("Please fix all time validation errors before saving.");
      return false;
    }

    const apiBlocks = (sessionFormatBlocks?.results || []) as any[];

    const blocksPayload = apiBlocks.map((blk) => {
      const parentId = String(blk._id);
      const blockType = blk.type || "MATRIX";
      const workouts = Array.isArray(blk.workouts) ? blk.workouts : [];

      // For GROUP blocks, get workout data from the GROUP entry's nested workouts
      if (blockType === "GROUP") {
        const groupEntry = groupBlockById.get(parentId);
        const groupWorkouts = groupEntry?.workouts || [];

        return {
          id: parentId,
          restTime: restTimeByParentBlock.get(parentId) ?? 0,
          workouts: workouts.map((w: any) => {
            const workoutId = String(w._id);
            // Find matching workout from the GROUP block's nested workouts
            const groupWorkout = groupWorkouts.find(
              (gw: any) => String(gw._id) === workoutId,
            );

            // Build metrics from the group workout's metrics array
            const metrics = (groupWorkout?.metrics || []).map((m: any) => ({
              metric: m.metric || m._id,
              value: Number(m.value ?? 0),
            }));

            return {
              id: workoutId,
              duration:
                typeof w.duration === "string"
                  ? toSeconds(w.duration)
                  : Number(w.duration || 0),
              metrics,
              workPeriod: groupWorkout?.workPeriod !== undefined
                ? toSeconds(groupWorkout.workPeriod)
                : Number(w.workPeriod || 0),
              restPeriod: groupWorkout?.restPeriod !== undefined
                ? toSeconds(groupWorkout.restPeriod)
                : Number(w.restPeriod || 0),
              blockTiming: groupWorkout?.blockTiming !== undefined
                ? toSeconds(groupWorkout.blockTiming)
                : Number(w.blockTiming || 0),
            };
          }),
        };
      }

      // For MATRIX/SINGLE blocks (existing logic)
      return {
        id: parentId,
        restTime: restTimeByParentBlock.get(parentId) ?? 0,
        workouts: workouts.map((w: any) => {
          const workoutId = String(w._id);
          const ui = uiByWorkoutId.get(workoutId);
          return {
            id: workoutId,
            duration:
              typeof w.duration === "string"
                ? toSeconds(w.duration)
                : Number(w.duration || 0),
            metrics: (ui?.fields || []).map((metricId) => ({
              metric: metricId,
              value: Number(ui?.fieldValues?.[metricId] ?? 0),
            })),
            workPeriod:
              ui?.workPeriod !== undefined
                ? toSeconds(ui.workPeriod)
                : Number(w.workPeriod || 0),
            restPeriod:
              ui?.restPeriod !== undefined
                ? toSeconds(ui.restPeriod)
                : Number(w.restPeriod || 0),
            blockTiming:
              ui?.blockTiming !== undefined
                ? toSeconds(ui.blockTiming)
                : Number(w.blockTiming || 0),
          };
        }),
      };
    });

    try {
      if (isDirty) {
        await createMatrice({
          sessionFormatId: selectedFormatId,
          difficulty: effectiveDifficulty ?? undefined,
          blocks: blocksPayload,
        }).unwrap();
      }

      setIsDirty(false);

      const finalAction = overridingAction || pendingAction;
      if (finalAction) {
        if (finalAction.type === "FORMAT") {
          setSelectedFormatIdx(finalAction.index!);
          setSelectedDifficulty(null);
        } else if (finalAction.type === "DIFFICULTY") {
          setSelectedFormatIdx(finalAction.index!);
          setSelectedDifficulty(finalAction.difficulty!);
        } else if (finalAction.type === "CANCEL") {
          navigate(adminRoutes.sessionList);
        } else if (finalAction.type === "PREVIOUS") {
          navigate(
            `${adminRoutes.sessionBlocksBuilder.replace(
              ":sessionid",
              String(sessionid || ""),
            )}${categoryId ? `?category=${encodeURIComponent(categoryId)}` : ""}`,
          );
        } else if (finalAction.type === "SUMMARY") {
          navigate(
            `${adminRoutes.sessionSummaryBuilder.replace(
              ":sessionid",
              String(sessionid || ""),
            )}${categoryId ? `?category=${encodeURIComponent(categoryId)}` : ""}`,
          );
        }
        setPendingAction(null);
        setShowUnsavedModal(false);
        return true;
      }

      // Auto-advance to next format; if last, go to Session Summary
      const isLastFormat = selectedFormatIdx >= formatList.length - 1;
      if (!isLastFormat) {
        setSelectedFormatIdx((i) => Math.min(i + 1, formatList.length - 1));
        setSelectedDifficulty(null);
        setFormatBlocks([]);
      } else {
        navigate(
          `${adminRoutes.sessionSummaryBuilder.replace(
            ":sessionid",
            String(sessionid || ""),
          )}${categoryId ? `?category=${encodeURIComponent(categoryId)}` : ""}`,
        );
      }
      return true;
    } catch (e) {
      console.error("Matrix submission failed:", e);
      return false;
    }
  };

  const progressPct = 66;

  // Click handlers
  const handleFormatSwitch = (newFormatIdx: number) => {
    if (newFormatIdx === selectedFormatIdx) return;

    if (hasTimeErrors) {
      toast.error("Please fix all time validation errors before switching.");
      return;
    }

    if (!allBlocksHaveMetrics(formatBlocks) || !allTimesFilled(formatBlocks)) {
      setShowValidationErrors(true);
      if (!allTimesFilled(formatBlocks)) {
        toast.error(
          "Please ensure all workout work and rest periods have valid time values before switching.",
        );
      }
      return;
    }

    if (isDirty) {
      setPendingAction({ type: "FORMAT", index: newFormatIdx });
      setShowUnsavedModal(true);
      return;
    }
    setFormatBlocks([]);
    setSelectedFormatIdx(newFormatIdx);
    setSelectedDifficulty(null);
    setShowValidationErrors(false);
  };
  const handleSelectDifficulty = (
    idx: number,
    level: "BEGINNER" | "ADVANCED",
  ) => {
    if (selectedFormatIdx === idx && selectedDifficulty === level) return;

    if (hasTimeErrors) {
      toast.error("Please fix all time validation errors before switching.");
      return;
    }

    if (!allBlocksHaveMetrics(formatBlocks) || !allTimesFilled(formatBlocks)) {
      setShowValidationErrors(true);
      if (!allTimesFilled(formatBlocks)) {
        toast.error(
          "Please ensure all workout work and rest periods have valid time values before switching.",
        );
      }
      return;
    }

    if (isDirty) {
      setPendingAction({ type: "DIFFICULTY", index: idx, difficulty: level });
      setShowUnsavedModal(true);
      return;
    }
    setFormatBlocks([]);
    if (selectedFormatIdx !== idx) setSelectedFormatIdx(idx);
    setSelectedDifficulty(level);
    setShowValidationErrors(false);
  };

  const handleDiscard = () => {
    setIsDirty(false);
    setShowUnsavedModal(false);
    if (pendingAction) {
      if (pendingAction.type === "FORMAT") {
        setFormatBlocks([]);
        setSelectedFormatIdx(pendingAction.index!);
        setSelectedDifficulty(null);
      } else if (pendingAction.type === "DIFFICULTY") {
        setFormatBlocks([]);
        setSelectedFormatIdx(pendingAction.index!);
        setSelectedDifficulty(pendingAction.difficulty!);
      } else if (pendingAction.type === "CANCEL") {
        navigate(adminRoutes.sessionList);
      } else if (pendingAction.type === "PREVIOUS") {
        navigate(
          `${adminRoutes.sessionBlocksBuilder.replace(
            ":sessionid",
            String(sessionid || ""),
          )}${categoryId ? `?category=${encodeURIComponent(categoryId)}` : ""}`,
        );
      } else if (pendingAction.type === "SUMMARY") {
        navigate(
          `${adminRoutes.sessionSummaryBuilder.replace(
            ":sessionid",
            String(sessionid || ""),
          )}${categoryId ? `?category=${encodeURIComponent(categoryId)}` : ""}`,
        );
      }
      setPendingAction(null);
    }
  };

  return (
    <div
      className={`pt-20 pb-8 min-h-screen ${themeBox ? "bg-[#0B0B0B]" : "bg-gray-50"
        } px-4 md:px-6 lg:px-8`}
      style={{
        marginLeft: sidebarWidth,
        width: `calc(100% - ${sidebarWidth}px)`,
      }}
    >
      {/* Header */}
      <div className="mb-4">
        <h1
          className={`text-xl font-semibold ${themeBox ? "text-gray-100" : "text-gray-900"
            }`}
        >
          Session Overview
        </h1>
        <div className="flex items-center gap-2 mt-2 text-sm">
          <Link
            to={`${adminRoutes.sessionList}`}
            onClick={(e) => {
              if (isDirty) {
                e.preventDefault();
                setPendingAction({ type: "CANCEL" });
                setShowUnsavedModal(true);
              }
            }}
            className={theme === "dark" ? "text-gray-400" : "text-gray-600"}
          >
            All Sessions
          </Link>
          <span
            className={theme === "dark" ? "text-gray-400" : "text-gray-600"}
          >
            /
          </span>
          <span
            className={theme === "dark" ? "text-[#B6CB24]" : "text-lime-600"}
          >
            {isSessionFormatBlocksExist ? "Update Session" : "Create New Session"}
          </span>
        </div>
      </div>

      {/* Card */}
      <div
        className={`rounded-lg border ${bgCard} relative overflow-hidden`}
        style={{ borderColor }}
      >
        {/* Header Row with progress bar */}
        <div className={`flex border-b ${themeBox ? "border-[#232323]" : "border-gray-200"}`}>
          <div className={`w-64 px-5 py-5 font-semibold text-sm ${themeBox ? "text-gray-100" : "text-gray-800"} border-r ${themeBox ? "border-[#232323]" : "border-gray-200"}`}>
            Session Summary
          </div>
          <div className="flex-1 px-6 py-4">
            <div className={`font-semibold text-sm mb-3 ${themeBox ? "text-gray-100" : "text-gray-800"}`}>
              {isSessionFormatBlocksExist ? "Update Session" : "Create New Session"}
            </div>
            <div className={`h-1.5 w-full ${themeBox ? "bg-[#1A1A1A]" : "bg-gray-200"} rounded-full overflow-hidden`}>
              <div
                className="h-full bg-[#B6CB24] transition-all duration-500 ease-out shadow-[0_0_8px_rgba(182,203,36,0.4)]"
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>
        </div>

        <div className="flex">
          {/* Sidebar: Formats */}
          <div
            className={`flex flex-col w-64 border-r ${themeBox ? "border-[#232323]" : "border-gray-200"
              } py-4`}
          >
            <h4
              className={`px-4 pb-6 text-sm font-semibold ${themeBox ? "text-gray-100" : "text-gray-800"
                }`}
            >
              Session Summary
            </h4>
            <div className="px-5 relative">
              {/* Vertical timeline line - stops at the last dot */}
              <div className="absolute left-[27px] top-2 bottom-[30px] w-0 border-l border-dashed border-[#444]" />

              <div className="space-y-6">
                {formatList.map((fmt: any, idx: number) => {
                  const isActive = selectedFormatIdx === idx;
                  const hasDifficulties =
                    Array.isArray(fmt?.format?.difficulties) &&
                    fmt.format.difficulties.length > 0;

                  return (
                    <div key={fmt._id} className="relative z-10">
                      <div className="flex items-start gap-4">
                        {/* Timeline Dot */}
                        <div
                          className={`mt-2.5 w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-all ${isActive
                            ? "border-[#B6CB24] bg-[#B6CB24] shadow-[0_0_8px_rgba(182,203,36,0.6)]"
                            : themeBox
                              ? "border-[#444] bg-[#121212]"
                              : "border-gray-300 bg-white"
                            }`}
                        >
                          {isActive && (
                            <div className="w-1.5 h-1.5 rounded-full bg-black/20" />
                          )}
                        </div>

                        {/* Format Content */}
                        <div className="flex-1 min-w-0">
                          <button
                            onClick={() => handleFormatSwitch(idx)}
                            className={`w-full text-left px-4 py-3 rounded-xl border transition-all ${isActive
                              ? themeBox
                                ? "bg-[#B6CB24]/10 border-[#B6CB24] text-[#F3F3F6]"
                                : "bg-lime-50 border-lime-500 text-lime-900"
                              : themeBox
                                ? "bg-transparent border-[#232323] text-gray-400 hover:border-[#444]"
                                : "bg-transparent border-gray-200 text-gray-600 hover:border-gray-300"
                              }`}
                          >
                            <span className="text-sm font-semibold truncate block">
                              {fmt.name}
                            </span>
                          </button>

                          {/* Difficulty sub-items aligned under format name */}
                          {hasDifficulties && isActive && (
                            <div className="mt-4 flex flex-col items-center space-y-3">
                              <button
                                type="button"
                                onClick={() =>
                                  handleSelectDifficulty(idx, "BEGINNER")
                                }
                                className={`text-sm transition-colors ${selectedDifficulty === "BEGINNER"
                                  ? "text-[#B6CB24] font-bold"
                                  : "text-gray-500 hover:text-gray-400"
                                  }`}
                              >
                                Beginner
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  handleSelectDifficulty(idx, "ADVANCED")
                                }
                                className={`text-sm transition-colors ${selectedDifficulty === "ADVANCED"
                                  ? "text-[#B6CB24] font-bold"
                                  : "text-gray-500 hover:text-gray-400"
                                  }`}
                              >
                                Advanced
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Session Summary as final node */}
                <div className="relative z-10">
                  <div className="flex items-start gap-4">
                    <div
                      className={`mt-2.5 w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-all ${themeBox
                        ? "border-[#444] bg-[#121212]"
                        : "border-gray-300 bg-white"
                        }`}
                    />
                    <div className="flex-1 min-w-0">
                      <button
                        className={`w-full text-left px-4 py-3 rounded-xl border transition-all ${themeBox
                          ? "bg-transparent border-[#232323] text-gray-400 hover:border-[#444]"
                          : "bg-transparent border-gray-200 text-gray-600 hover:border-gray-300"
                          }`}
                        onClick={() => {
                          if (isDirty) {
                            setPendingAction({ type: "SUMMARY" });
                            setShowUnsavedModal(true);
                            return;
                          }
                          navigate(
                            `${adminRoutes.sessionSummaryBuilder.replace(
                              ":sessionid",
                              String(sessionid || ""),
                            )}${categoryId
                              ? `?category=${encodeURIComponent(categoryId)}`
                              : ""
                            }`,
                          )
                        }
                        }
                      >
                        <span className="text-sm font-semibold truncate block">
                          Session Summary
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Matrix content */}
          <div className="flex-1">
            <MatricSessionFormat
              key={`${selectedFormatId}-${selectedDifficulty}`}
              themeBox={themeBox}
              formatBlocks={formatBlocks as any}
              setFormatBlocks={setFormatBlocks as any}
              sessionFormatBlocks={sessionFormatBlocks?.results || []}
              difficulty={effectiveDifficulty}
              sessionFormatId={selectedFormatId || ""} // send format id
              setIsDirty={setIsDirty}
              setHasTimeErrors={setHasTimeErrors}
              showValidationErrors={showValidationErrors}
            />
          </div>
        </div>

        {/* Footer: Actions */}
        <div
          className={`flex items-center justify-center px-6 py-4 border-t ${themeBox ? "border-[#232323]" : "border-gray-200"
            }`}
        >
          <div className="flex items-center gap-4">
            <button
              onClick={() => onSaveMatrix()}
              disabled={isSubmitting}
              className={`flex items-center gap-2 px-5 py-1.5 rounded-[10px] border border-[#C6D92C] text-black font-medium hover:opacity-95 disabled:opacity-60 disabled:cursor-not-allowed ${theme === "dark"
                ? "bg-[#232323] text-gray-200 hover:bg-[#333333]"
                : "bg-gray-200 text-gray-800 hover:bg-gray-300"
                }`}
              style={{
                background:
                  "radial-gradient(94.17% 100% at 52.48% 0%, rgba(198,217,44,0.80) 0%, rgba(83,91,16,0.80) 100%)",
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M19 21C19.6068 20.4102 22 18.8403 22 18C22 17.1597 19.6068 15.5898 19 15M21 18H14"
                  stroke="#C6D92C"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M12 21C7.28595 21 4.92893 21 3.46447 19.5355C2 18.0711 2 15.714 2 11V7.94427C2 6.1278 2 5.21956 2.38032 4.53806C2.65142 4.05227 3.05227 3.65142 3.53806 3.38032C4.21956 3 5.1278 3 6.94427 3C8.10802 3 8.6899 3 9.19926 3.19101C10.3622 3.62712 10.8418 4.68358 11.3666 5.73313L12 7M8 7H16.75C18.8567 7 19.91 7 20.6667 7.50559C20.9943 7.72447 21.2755 8.00572 21.4944 8.33329C21.9796 9.05942 21.9992 10.0588 22 12V13"
                  stroke="#F3F3F6"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
              {isSubmitting
                ? "Saving..."
                : isSessionFormatBlocksExist
                  ? "Update & Next"
                  : "Save & Next"}
            </button>
            <button
              className={`flex items-center gap-2 px-5 py-2 rounded-md text-sm font-medium border ${themeBox
                ? "border-[#444] text-gray-300 hover:bg-[#1E1E1E]"
                : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              onClick={() => {
                if (isDirty) {
                  setPendingAction({ type: "PREVIOUS" });
                  setShowUnsavedModal(true);
                  return;
                }
                navigate(
                  `${adminRoutes.sessionBlocksBuilder.replace(
                    ":sessionid",
                    String(sessionid || ""),
                  )}${categoryId
                    ? `?category=${encodeURIComponent(categoryId)}`
                    : ""
                  }`,
                )
              }
              }
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M11 6H15.5C17.9853 6 20 8.01472 20 10.5C20 12.9853 17.9853 15 15.5 15H4"
                  stroke={themeBox ? "#F3F3F6" : "#C6D92C"}
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M6.99998 12C6.99998 12 4.00001 14.2095 4 15C3.99999 15.7906 7 18 7 18"
                  stroke={themeBox ? "#F3F3F6" : "#C6D92C"}
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Previous
            </button>

            <button
              onClick={() => {
                if (isDirty) {
                  setPendingAction({ type: "CANCEL" });
                  setShowUnsavedModal(true);
                  return;
                }
                navigate(adminRoutes.sessionList)
              }
              }
              className={`flex items-center gap-2 px-5 py-2 rounded-md text-sm font-medium border ${themeBox
                ? "border-[#444] text-gray-300 hover:bg-[#1E1E1E]"
                : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M12 21.5C16.4783 21.5 18.7175 21.5 20.1088 20.1088C21.5 18.7175 21.5 16.4783 21.5 12C21.5 7.52165 21.5 5.28248 20.1088 3.89124C18.7175 2.5 16.4783 2.5 12 2.5C7.52166 2.5 5.28249 2.5 3.89124 3.89124C2.5 5.28249 2.5 7.52166 2.5 12C2.5 16.4783 2.5 18.7175 3.89124 20.1088C5.28248 21.5 7.52165 21.5 12 21.5Z"
                  stroke={themeBox ? "#F3F3F6" : "#C6D92C"}
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M15 9L9 14.9996M15 15L9 9.00039"
                  stroke={themeBox ? "#F3F3F6" : "#C6D92C"}
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Cancel
            </button>
          </div>
        </div>
      </div>
      <UnsavedChangesModal
        open={showUnsavedModal}
        onClose={() => {
          setShowUnsavedModal(false);
          setPendingAction(null);
        }}
        themeBox={themeBox}
        onSave={() => onSaveMatrix()}
        onDiscard={handleDiscard}
        isSaving={isSubmitting}
      />
    </div>
  );
};

export default SessionMetricsBuilder;
